/**
 * StockEntryTimelineSection — vertical timeline of a Stock Entry's
 * lifecycle milestones: creation → posted → last modified. Bilingual,
 * Thai-SME-friendly relative dates so owners can see "2 days ago" at a
 * glance instead of parsing ISO strings.
 *
 * Read-only. This is a summary of audit fields, not a journal editor.
 *
 * Design language matches the sibling cards:
 *   - uppercase pl-4 section header
 *   - CARD token from design-tokens.ts
 *   - dot + vertical line timeline with text-right date stamps
 */

import { CheckCircle2, Clock, FileText, Pencil, XCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/i18n/useLanguage";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";

const i18n: Record<string, { en: string; th: string }> = {
  timeline: { en: "Timeline", th: "ประวัติเอกสาร" },
  created: { en: "Created", th: "สร้างเอกสาร" },
  created_by: { en: "by", th: "โดย" },
  posted: { en: "Posted", th: "วันที่บันทึก" },
  submitted: { en: "Submitted", th: "ส่งยืนยันแล้ว" },
  cancelled: { en: "Cancelled", th: "ยกเลิกแล้ว" },
  modified: { en: "Last modified", th: "แก้ไขล่าสุด" },
  just_now: { en: "just now", th: "เมื่อครู่" },
  minutes_ago: { en: "m ago", th: "นาทีที่แล้ว" },
  hours_ago: { en: "h ago", th: "ชั่วโมงที่แล้ว" },
  days_ago: { en: "d ago", th: "วันที่แล้ว" },
  weeks_ago: { en: "w ago", th: "สัปดาห์ที่แล้ว" },
};

function formatAbsolute(value: string | undefined, language: "en" | "th"): string {
  if (!value) return "—";
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(language === "th" ? "th-TH" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatRelative(
  value: string | undefined,
  t: (k: string) => string,
  language: "en" | "th",
): string {
  if (!value) return "";
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / (60 * 1000));
  if (diffMin < 1) return t("just_now");
  if (diffMin < 60) {
    return language === "th" ? `${diffMin} ${t("minutes_ago")}` : `${diffMin}${t("minutes_ago")}`;
  }
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) {
    return language === "th" ? `${diffHour} ${t("hours_ago")}` : `${diffHour}${t("hours_ago")}`;
  }
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) {
    return language === "th" ? `${diffDay} ${t("days_ago")}` : `${diffDay}${t("days_ago")}`;
  }
  const diffWeek = Math.floor(diffDay / 7);
  return language === "th" ? `${diffWeek} ${t("weeks_ago")}` : `${diffWeek}${t("weeks_ago")}`;
}

interface TimelineItem {
  icon: LucideIcon;
  iconClass: string;
  label: string;
  absolute: string;
  relative: string;
  subtitle?: string;
}

function TimelineRow({
  item,
  isLast,
}: {
  item: TimelineItem;
  isLast: boolean;
}) {
  const Icon = item.icon;
  return (
    <div className="flex gap-3">
      {/* Dot + line column */}
      <div className="flex flex-col items-center shrink-0">
        <div className={`flex h-7 w-7 items-center justify-center rounded-full ${item.iconClass}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        {isLast ? null : <div className="w-px flex-1 bg-border/60 mt-1" />}
      </div>

      {/* Content column */}
      <div className={`flex-1 min-w-0 ${isLast ? "" : "pb-4"}`}>
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-sm font-medium text-foreground truncate">{item.label}</div>
          <div className="text-xs text-muted-foreground shrink-0 tabular-nums">
            {item.relative}
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">
          {item.absolute}
          {item.subtitle ? <span className="ml-1">· {item.subtitle}</span> : null}
        </div>
      </div>
    </div>
  );
}

export function StockEntryTimelineSection({ formData }: ShopifySectionWidgetProps) {
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };
  const lang = language === "th" ? "th" : "en";

  const creation = (formData.creation as string) || "";
  const owner = (formData.owner as string) || "";
  const postingDate = (formData.posting_date as string) || "";
  const modified = (formData.modified as string) || "";
  const modifiedBy = (formData.modified_by as string) || "";
  const docstatus = Number(formData.docstatus ?? 0);

  const items: TimelineItem[] = [];

  // Creation — always present
  if (creation) {
    items.push({
      icon: FileText,
      iconClass: "bg-muted text-muted-foreground",
      label: t("created"),
      absolute: formatAbsolute(creation, lang),
      relative: formatRelative(creation, t, lang),
      subtitle: owner ? `${t("created_by")} ${owner}` : undefined,
    });
  }

  // Posted — use posting_date (date only, no time component)
  if (postingDate) {
    items.push({
      icon: Clock,
      iconClass: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
      label: t("posted"),
      absolute: formatAbsolute(postingDate, lang),
      relative: formatRelative(postingDate, t, lang),
    });
  }

  // Submitted / Cancelled state — implied by modified time when docstatus changed
  if (docstatus === 1) {
    items.push({
      icon: CheckCircle2,
      iconClass: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
      label: t("submitted"),
      absolute: formatAbsolute(modified, lang),
      relative: formatRelative(modified, t, lang),
      subtitle: modifiedBy ? `${t("created_by")} ${modifiedBy}` : undefined,
    });
  } else if (docstatus === 2) {
    items.push({
      icon: XCircle,
      iconClass: "bg-destructive/10 text-destructive",
      label: t("cancelled"),
      absolute: formatAbsolute(modified, lang),
      relative: formatRelative(modified, t, lang),
      subtitle: modifiedBy ? `${t("created_by")} ${modifiedBy}` : undefined,
    });
  } else if (modified && modified !== creation) {
    // Draft that has been edited since creation — show as "Last modified"
    items.push({
      icon: Pencil,
      iconClass: "bg-muted text-muted-foreground",
      label: t("modified"),
      absolute: formatAbsolute(modified, lang),
      relative: formatRelative(modified, t, lang),
      subtitle: modifiedBy ? `${t("created_by")} ${modifiedBy}` : undefined,
    });
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
        {t("timeline")}
      </h3>
      <Card className={CARD}>
        <CardContent className="p-5">
          <div className="flex flex-col">
            {items.map((item, idx) => (
              <TimelineRow
                key={`${item.label}-${idx}`}
                item={item}
                isLast={idx === items.length - 1}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
